/** Shared parsing for Chat ABAP HTTP API responses */

import axios from 'axios';
import { isHiddenChatMessage, isProcessingPlaceholder, STUDIO_WELCOME_MESSAGE } from '@/lib/chatDisplay';

/** Query param used by Vibe Coding Studio and Chat history for session restore. */
export const VIBE_CHAT_SESSION_QUERY = 'session';

/** Survives in-app navigation when the URL query param is cleared. */
export const VIBE_CHAT_ACTIVE_SESSION_STORAGE_KEY = 'vibe-studio.activeChatSessionId';

export function readChatSessionIdFromSearch(search) {
  if (!search) return '';
  if (typeof search.get === 'function') {
    return String(search.get(VIBE_CHAT_SESSION_QUERY) || '').trim();
  }
  return '';
}

export function buildChatSessionSearchParams(sessionId) {
  const id = String(sessionId || '').trim();
  return id ? { [VIBE_CHAT_SESSION_QUERY]: id } : {};
}

export function readActiveChatSessionIdFromStorage() {
  try {
    return String(sessionStorage.getItem(VIBE_CHAT_ACTIVE_SESSION_STORAGE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function persistActiveChatSessionId(sessionId) {
  const id = String(sessionId || '').trim();
  try {
    if (id) {
      sessionStorage.setItem(VIBE_CHAT_ACTIVE_SESSION_STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(VIBE_CHAT_ACTIVE_SESSION_STORAGE_KEY);
    }
  } catch {
    /* private mode / quota */
  }
}

export function clearActiveChatSessionId() {
  persistActiveChatSessionId('');
}

/** Whether async graph work is likely still running after a thread reload. */
export function inferGeneratingFromThreadMeta(meta, status) {
  if (status && status.generating != null) {
    return Boolean(status.generating);
  }
  if (meta && meta.generating != null) {
    return Boolean(meta.generating);
  }
  const phase = String(meta?.phase || '').toUpperCase();
  if (phase === 'GENERATE' || phase === 'REVISE') {
    return true;
  }
  return false;
}

export function fallbackMessagesForEmptyThread(meta, status) {
  if (status?.message && String(status.message).trim()) {
    return [{ role: 'assistant', content: String(status.message).trim() }];
  }
  if (!meta?.phase || String(meta.phase).toUpperCase() === 'GATHER') {
    return [{ role: 'assistant', content: STUDIO_WELCOME_MESSAGE }];
  }
  // Never leave the thread visually empty after a session switch — status/message
  // stores can lag behind phase for workflow/security completions.
  const phase = String(meta?.phase || status?.phase || '').toUpperCase();
  if (phase === 'DONE') {
    return [
      {
        role: 'assistant',
        content: 'This chat has finished. Send a follow-up below, or start a New chat.',
      },
    ];
  }
  if (phase === 'CONFIRM') {
    return [
      {
        role: 'assistant',
        content: 'Working on your request — this page will update when there is a result.',
      },
    ];
  }
  return [{ role: 'assistant', content: STUDIO_WELCOME_MESSAGE }];
}

export function formatApiError(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || 'Request failed';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) {
    return d.detail.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('; ');
  }
  if (typeof d === 'object') return JSON.stringify(d);
  return String(d);
}

export function normalizeSessionsPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, total: null, has_more: false, limit: data.length, offset: 0 };
  }
  if (data && typeof data === 'object') {
    const raw =
      data.sessions ??
      data.items ??
      data.data ??
      data.results ??
      data.records ??
      [];
    const items = Array.isArray(raw) ? raw : [];
    const total =
      typeof data.total === 'number'
        ? data.total
        : typeof data.count === 'number'
          ? data.count
          : typeof data.total_count === 'number'
            ? data.total_count
            : null;
    return {
      items,
      total,
      has_more: Boolean(data.has_more),
      limit: typeof data.limit === 'number' ? data.limit : items.length,
      offset: typeof data.offset === 'number' ? data.offset : 0,
    };
  }
  return { items: [], total: null, has_more: false, limit: 0, offset: 0 };
}

export function sessionIdOf(row) {
  if (!row || typeof row !== 'object') return '';
  return (
    row.session_id ??
    row.sessionId ??
    row.id ??
    row._id ??
    row.uuid ??
    ''
  );
}

export function phaseOf(row) {
  const p = row?.phase ?? row?.status ?? row?.state ?? '';
  return typeof p === 'string' ? p : String(p || '');
}

export function previewOf(row) {
  const v =
    row?.last_message ??
    row?.lastMessage ??
    row?.preview ??
    row?.summary ??
    row?.title ??
    row?.message ??
    '';
  if (typeof v !== 'string') {
    try {
      return JSON.stringify(v).slice(0, 200);
    } catch {
      return '';
    }
  }
  return v;
}

/** Normalize a user-edited chat title (trim + max 80). Returns '' if blank. */
export function normalizeChatTitle(title) {
  const cleaned = String(title ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned.slice(0, 80);
}

export function titleOf(row) {
  const t = row?.title;
  if (typeof t === 'string' && t.trim()) return t.trim();
  return '';
}

/**
 * PATCH /chat/{sessionId}/title — locks title_user_set on the server so auto-summary
 * never overwrites the user's name.
 */
export async function renameChatSession(apiBase, sessionId, title) {
  const base = (apiBase || '').replace(/\/$/, '');
  const id = String(sessionId || '').trim();
  const normalized = normalizeChatTitle(title);
  if (!base || !id) {
    throw new Error('renameChatSession requires apiBase and sessionId');
  }
  if (!normalized) {
    throw new Error('Title is required');
  }
  const { data } = await axios.patch(`${base}/chat/${id}/title`, { title: normalized });
  return {
    sessionId: data?.session_id ?? id,
    title: data?.title ?? normalized,
    titleUserSet: data?.title_user_set !== false,
  };
}

export function dateOf(row) {
  const d =
    row?.updated_at ??
    row?.updatedAt ??
    row?.modified_at ??
    row?.last_activity_at ??
    row?.created_at ??
    row?.createdAt ??
    null;
  if (!d) return null;
  const t = Date.parse(d);
  return Number.isNaN(t) ? null : new Date(t);
}

function normalizeOneMessage(m) {
  if (m == null) return { role: 'assistant', content: '' };
  if (typeof m === 'string') return { role: 'assistant', content: m };
  if (typeof m !== 'object') return { role: 'assistant', content: String(m) };
  const roleRaw = m.role ?? m.sender ?? m.type ?? m.author ?? 'assistant';
  const r = String(roleRaw).toLowerCase();
  const isUser = r === 'user' || r === 'human' || r === 'customer';
  const content = m.content ?? m.text ?? m.body ?? m.message ?? '';
  const text = typeof content === 'string' ? content : JSON.stringify(content ?? '');
  const generatedCode = m.generated_code ?? m.generatedCode ?? null;
  const phase = m.phase ?? null;
  const awaitingUser = m.awaiting_user ?? m.awaitingUser;
  return {
    role: isUser ? 'user' : 'assistant',
    content: text,
    generatedCode: generatedCode || null,
    phase: phase ? String(phase) : null,
    awaiting_user: awaitingUser === undefined || awaitingUser === null ? null : Boolean(awaitingUser),
  };
}

/** Strip ```abap fences from stored generated_code. */
export function stripCodeFences(code) {
  if (code == null) return null;
  const trimmed = String(code).trim();
  if (!trimmed) return null;
  const fenced = trimmed.match(/^```(?:abap)?\s*([\s\S]*?)```$/i);
  return fenced ? fenced[1].trim() : trimmed;
}

export function deriveThreadMetaFromMessages(messages) {
  let phase = '';
  let awaitingUser = null;
  let generatedCode = null;

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!phase && m.phase) {
      phase = String(m.phase).toUpperCase();
    }
    if (!generatedCode && m.generatedCode && String(m.generatedCode).trim()) {
      generatedCode = stripCodeFences(m.generatedCode);
    }
    if (m.role === 'assistant' && awaitingUser === null && m.awaiting_user != null) {
      awaitingUser = Boolean(m.awaiting_user);
    }
  }

  if (generatedCode && !phase) {
    phase = 'DONE';
  }
  if (phase === 'DONE') {
    awaitingUser = false;
  }

  return {
    phase,
    awaiting_user: awaitingUser,
    generated_code: generatedCode,
  };
}

function mergeThreadMeta(...sources) {
  const out = {
    phase: '',
    awaiting_user: null,
    generated_code: null,
    artifact_summary: null,
    prd_available: false,
    use_studio_context: false,
    deploy_available: false,
    code_validated: false,
    flow_version: 'v1',
    intent: '',
    studio_context_locked: false,
    studio_context_chosen: false,
    artifact_type: null,
    artifact_url: null,
    confirm_doc_type: null,
    generating: null,
    workflow_stage: null,
    workflow_state: null,
    ticket_number: null,
    awaiting_action: null,
    chain_of_thought: null,
    panel_blocks: null,
    artifacts: null,
    active_artifact_id: null,
    intent_meta: null,
  };
  for (const src of sources) {
    if (!src) continue;
    if (src.phase) out.phase = String(src.phase).toUpperCase();
    if (src.generating != null) out.generating = Boolean(src.generating);
    const au = src.awaiting_user ?? src.awaitingUser;
    if (au !== undefined && au !== null) out.awaiting_user = Boolean(au);
    const gc = src.generated_code ?? src.generatedCode;
    if (gc !== undefined && gc !== null) {
      const trimmed = String(gc).trim();
      out.generated_code = trimmed ? stripCodeFences(trimmed) : null;
    }
    const summary = src.artifact_summary ?? src.artifactSummary;
    if (summary != null && String(summary).trim() !== '') {
      out.artifact_summary = String(summary);
    }
    if (src.prd_available ?? src.prdAvailable) {
      out.prd_available = true;
    }
    if (src.use_studio_context ?? src.useStudioContext) {
      out.use_studio_context = true;
    }
    if (src.deploy_available ?? src.deployAvailable) {
      out.deploy_available = true;
    }
    if (src.code_validated ?? src.codeValidated) {
      out.code_validated = true;
    }
    if (src.flow_version ?? src.flowVersion) {
      out.flow_version = String(src.flow_version ?? src.flowVersion);
    }
    if (src.intent != null && String(src.intent).trim() !== '') {
      out.intent = String(src.intent);
    }
    const intentMeta = src.intent_meta ?? src.intentMeta;
    if (intentMeta && typeof intentMeta === 'object') {
      out.intent_meta = intentMeta;
    }
    if (src.studio_context_locked ?? src.studioContextLocked) {
      out.studio_context_locked = true;
    }
    if (src.studio_context_chosen ?? src.studioContextChosen) {
      out.studio_context_chosen = true;
    }
    const artifactUrl = src.artifact_url ?? src.artifactUrl;
    if (artifactUrl) out.artifact_url = String(artifactUrl);
    const artifactType = src.artifact_type ?? src.artifactType;
    if (artifactType) out.artifact_type = String(artifactType);
    const cdt = src.confirm_doc_type ?? src.confirmDocType;
    if (cdt) out.confirm_doc_type = String(cdt);
    const wfStage = src.workflow_stage ?? src.workflowStage;
    if (wfStage) out.workflow_stage = String(wfStage);
    const wfState = src.workflow_state ?? src.workflowState;
    if (wfState && typeof wfState === 'object') out.workflow_state = wfState;
    const tn = src.ticket_number ?? src.ticketNumber;
    if (tn) out.ticket_number = String(tn);
    const awaiting = src.awaiting_action ?? src.awaitingAction;
    if (awaiting) out.awaiting_action = String(awaiting);
    const cot = src.chain_of_thought ?? src.chainOfThought;
    if (cot != null && String(cot).trim() !== '') {
      out.chain_of_thought = String(cot);
    }
    const panels = src.panel_blocks ?? src.panelBlocks;
    if (Array.isArray(panels) && panels.length) {
      out.panel_blocks = panels;
    }
    const artifacts = src.artifacts;
    if (Array.isArray(artifacts) && artifacts.length) {
      out.artifacts = artifacts;
    }
    const activeAid = src.active_artifact_id ?? src.activeArtifactId;
    if (activeAid) out.active_artifact_id = String(activeAid);
  }
  return out;
}

export function normalizeMessagesPayload(data) {
  let raw = [];
  let meta = {};

  if (Array.isArray(data)) {
    raw = data;
  } else if (data && typeof data === 'object') {
    raw =
      data.messages ??
      data.items ??
      data.thread ??
      data.data ??
      data.history ??
      [];
    if (!Array.isArray(raw)) raw = [];
    meta = {
      phase: data.phase ?? data.state,
      generating: data.generating,
      awaiting_user: data.awaiting_user ?? data.awaitingUser,
      generated_code: data.generated_code ?? data.generatedCode,
      artifact_summary: data.artifact_summary ?? data.artifactSummary,
      prd_available: data.prd_available ?? data.prdAvailable,
      use_studio_context: data.use_studio_context ?? data.useStudioContext,
      deploy_available: data.deploy_available ?? data.deployAvailable,
      code_validated: data.code_validated ?? data.codeValidated,
      flow_version: data.flow_version ?? data.flowVersion,
      intent: data.intent,
      studio_context_locked: data.studio_context_locked ?? data.studioContextLocked,
      studio_context_chosen: data.studio_context_chosen ?? data.studioContextChosen,
      artifact_url: data.artifact_url ?? data.artifactUrl,
      artifact_type: data.artifact_type ?? data.artifactType,
      confirm_doc_type: data.confirm_doc_type ?? data.confirmDocType,
      workflow_stage: data.workflow_stage ?? data.workflowStage,
      workflow_state: data.workflow_state ?? data.workflowState,
      ticket_number: data.ticket_number ?? data.ticketNumber,
      awaiting_action: data.awaiting_action ?? data.awaitingAction,
      chain_of_thought: data.chain_of_thought ?? data.chainOfThought,
      panel_blocks: data.panel_blocks ?? data.panelBlocks,
      artifacts: data.artifacts,
      active_artifact_id: data.active_artifact_id ?? data.activeArtifactId,
      intent_meta: data.intent_meta ?? data.intentMeta,
      session_id: data.session_id ?? data.sessionId,
    };
  }

  const messages = raw
    .map(normalizeOneMessage)
    .filter((m) => !isHiddenChatMessage(m) && (m.content !== '' || m.role === 'user'));
  const derived = deriveThreadMetaFromMessages(messages);
  meta = mergeThreadMeta(meta, derived);
  return { messages, meta };
}

export async function loadChatSessionThread(sessionId, apiBase) {
  if (!sessionId || !apiBase) {
    return { messages: [], meta: {}, status: null };
  }
  const base = String(apiBase).replace(/\/$/, '');
  const [msgRes, statusRes] = await Promise.all([
    axios.get(`${base}/chat/${sessionId}/messages`),
    axios.get(`${base}/chat/${sessionId}/status`).catch(() => null),
  ]);
  let { messages, meta: msgMeta } = normalizeMessagesPayload(msgRes.data);
  let meta = { ...msgMeta };
  let status = null;
  if (statusRes?.data) {
    status = mapChatResponse(statusRes.data);
    meta = mergeThreadMeta(meta, {
      phase: status.phase,
      awaiting_user: status.awaitingUser,
      generated_code: status.generatedCode,
      artifact_summary: status.artifactSummary,
      prd_available: status.prdAvailable,
      use_studio_context: status.useStudioContext,
      deploy_available: status.deployAvailable,
      code_validated: status.codeValidated,
      flow_version: status.flowVersion,
      intent: status.intent,
      studio_context_locked: status.studioContextLocked,
      studio_context_chosen: status.studioContextChosen,
      platform_default_connected_mode: status.platformDefaultConnectedMode,
      platform_mode_locked: status.platformModeLocked,
      artifact_url: status.artifactUrl,
      artifact_type: status.artifactType,
      confirm_doc_type: status.confirmDocType,
      workflow_stage: status.workflowStage,
      workflow_state: status.workflowState,
      awaiting_action: status.awaitingAction,
      chain_of_thought: status.chainOfThought,
      generating: status.generating,
      panel_blocks: status.panelBlocks,
      artifacts: status.artifacts,
      active_artifact_id: status.activeArtifactId,
      intent_meta: status.intentMeta,
    });
    // If the transcript store has no messages (e.g. created before databasehandler
    // was wired up), fall back to the last assistant text from the status endpoint
    // so sidebar clicks always show at least the most recent assistant message.
    if (messages.length === 0 && status.message) {
      const fallback = { role: 'assistant', content: String(status.message).trim() };
      if (!isHiddenChatMessage(fallback) && fallback.content) {
        messages = [fallback];
      }
    }
  }
  if (messages.length === 0) {
    messages = fallbackMessagesForEmptyThread(meta, status);
  }
  return { messages, meta, status };
}

export function mapChatResponse(data) {
  return {
    sessionId: data.session_id,
    awaitingUser: Boolean(data.awaiting_user),
    phase: data.phase ?? '',
    message: data.message ?? '',
    generatedCode: data.generated_code ?? null,
    artifactSummary: data.artifact_summary ?? null,
    prdAvailable: Boolean(data.prd_available),
    generating: Boolean(data.generating),
    useStudioContext: Boolean(data.use_studio_context),
    deployAvailable: Boolean(data.deploy_available),
    codeValidated: Boolean(data.code_validated),
    flowVersion: data.flow_version ?? 'v1',
    intent: data.intent != null && String(data.intent).trim() !== '' ? String(data.intent) : null,
    studioContextLocked: Boolean(data.studio_context_locked),
    studioContextChosen: Boolean(data.studio_context_chosen),
    platformDefaultConnectedMode: data.platform_default_connected_mode ?? data.platformDefaultConnectedMode,
    platformModeLocked: Boolean(data.platform_mode_locked ?? data.platformModeLocked),
    artifactType: data.artifact_type ?? null,
    artifactUrl: data.artifact_url ?? null,
    confirmDocType: data.confirm_doc_type ?? data.confirmDocType ?? null,
    intentMeta: data.intent_meta ?? data.intentMeta ?? null,
    workflowStage: data.workflow_stage ?? data.workflowStage ?? null,
    workflowState: data.workflow_state ?? data.workflowState ?? null,
    ticketNumber: data.ticket_number ?? data.ticketNumber ?? null,
    awaitingAction: data.awaiting_action ?? data.awaitingAction ?? null,
    chainOfThought: data.chain_of_thought ?? data.chainOfThought ?? null,
    panelBlocks: data.panel_blocks ?? data.panelBlocks ?? null,
    artifacts: data.artifacts ?? null,
    activeArtifactId: data.active_artifact_id ?? data.activeArtifactId ?? null,
  };
}

/** Poll GET /chat/{sessionId}/status until async graph work finishes (Halo gateway path). */
export async function pollChatUntilReady(apiBase, sessionId, { intervalMs = 2000, maxWaitMs = 180000 } = {}) {
  const base = (apiBase || '').replace(/\/$/, '');
  if (!base || !sessionId) {
    throw new Error('pollChatUntilReady requires apiBase and sessionId');
  }

  const started = Date.now();
  let last = null;

  while (Date.now() - started < maxWaitMs) {
    const { data } = await axios.get(`${base}/chat/${sessionId}/status`);
    last = mapChatResponse(data);
    if (!last.generating) {
      return last;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  if (last) return last;
  throw new Error('Chat status polling timed out');
}

export { isProcessingPlaceholder };
